import type { InputActionBarConfig } from '../components/InputActionBar/InputActionBar.types';
import type { AttachmentDisplayItem } from '../types/attachments';

/**
 * Minimal shape required to merge file-upload state into the action bar and input.
 * Shared by Add Comment, Reply, and Edit mode so we have a single merge implementation (DRY).
 */
export interface FileUploadBarShape {
  attachmentCount: number;
  attachments: AttachmentDisplayItem[];
  removeFile: (id: string) => void;
  setPreviewOpen: React.Dispatch<React.SetStateAction<boolean>>;
  uploadAreaRef: React.RefObject<HTMLDivElement | null>;
  triggerFileSelect: () => void;
  previewOpen: boolean;
  openCarousel: (index: number) => void;
  clearValueAndAttachments: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  fileUploadConfig: { accept: string; allowMultipleFiles: boolean };
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  keepExpandedForUpload: boolean;
  registerRefocusInput: (fn: (() => void) | null) => void;
}

/** Single implementation for merging file-upload state into action bar config (Add Comment, Reply, Edit). */
export function mergeActionBarWithFileUpload(
  base: InputActionBarConfig | undefined,
  fileUpload: FileUploadBarShape
): InputActionBarConfig {
  return {
    ...base,
    onCancelClick: fileUpload.clearValueAndAttachments,
    showUpload: true,
    onUploadClick: fileUpload.triggerFileSelect,
    attachmentCount: fileUpload.attachmentCount,
    onBadgeClick: () => fileUpload.setPreviewOpen(true),
    uploadAreaRef: fileUpload.uploadAreaRef,
    attachments: fileUpload.attachments,
    previewOpen: fileUpload.previewOpen,
    onClosePreview: () => fileUpload.setPreviewOpen(false),
    onRemoveAttachment: fileUpload.removeFile,
    onAttachmentFileClick: fileUpload.openCarousel
  };
}

/** Single implementation for file-upload input props passed to DivWithTextAreaSuggestions. */
export function getFileUploadInputPropsFrom(fileUpload: FileUploadBarShape): {
  hasAttachments: boolean;
  fileInputProps: {
    ref: FileUploadBarShape['fileInputRef'];
    accept: string;
    multiple: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  };
  registerRefocusInput: (fn: (() => void) | null) => void;
} {
  return {
    hasAttachments:
      fileUpload.attachmentCount > 0 || fileUpload.keepExpandedForUpload,
    fileInputProps: {
      ref: fileUpload.fileInputRef,
      accept: fileUpload.fileUploadConfig.accept,
      multiple: fileUpload.fileUploadConfig.allowMultipleFiles,
      onChange: fileUpload.handleFileInputChange
    },
    registerRefocusInput: fileUpload.registerRefocusInput
  };
}
